import { RFX_STATUS } from "utils/constants";
import { RFX_DEFAULT_TAB } from "./rfx-tab-config";
import type { RfxListTab } from "../types/rfx-list.type";

/**
 * Which Tenders & Quotes tab an RFx of a given status is listed under.
 *
 * This exists so a detail page can send the user BACK to the tab they came
 * from without anything having threaded that tab through the URL. Nothing
 * carries the originating tab today — `clientPaths.viewRfx` builds
 * `/account/:companyId/rfx/:rfxId/:slug/all`, which has no room for it — so
 * the RFx's own status is used to re-derive the answer.
 *
 * DELIBERATELY more specific than the backend's `current` bucket. Server-side
 * `RfxClientViewService::tendersQuotesTabMap()` puts ACTIVE, EVALUATION,
 * AWARDED, ON_HOLD and CANCELLED all in `current`, so an RFx in evaluation is
 * legitimately visible under BOTH Current and Evaluation. When a row lives in
 * two tabs, the narrower one is the better guess at where the user clicked
 * from: someone opening an evaluating RFx almost certainly did so from the
 * Evaluation tab, and landing them on Current would look like the back button
 * had lost their place.
 *
 * Statuses with no tab of their own (COMPLETE, SUSPENDED, and the invite-level
 * values sharing this enum — see the PRO-386 TODO on `RFX_STATUS`) fall through
 * to the default tab, which is where the list itself would show them.
 */
export const getRfxListTabForStatus = (
	status: string | null | undefined
): RfxListTab => {
	switch (status) {
		case RFX_STATUS.EVALUATION:
			return "evaluation";
		case RFX_STATUS.AWARDED:
			return "awarded";
		case RFX_STATUS.DRAFT:
			return "drafts";
		case RFX_STATUS.ARCHIVED:
			return "archived";
		default:
			return RFX_DEFAULT_TAB;
	}
};
